/**
 * Creating a module: the form's state, and the two calls that save it.
 *
 * @module router/programmes/CreateModuleRoute
 */

import React, { useState } from "react";

import { useNavigate } from "react-router";

import { CreateModuleForm } from "@/components/programmes/CreateModuleForm";
import { Button, useAsyncAction } from "@/components/ui";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import { can } from "@/server/authorization";
import type { Module } from "@/types";
import { uid } from "@/utils/uid";

export function CreateModuleRoute(): React.ReactElement {
  const navigate = useNavigate();
  const { client, session, profile, platformProgrammes, refreshSession } =
    useWorkspaceOutletContext();

  // Only programmes this account may write to, and only unlocked ones: a
  // module cannot be created in a programme that can merely be read, and
  // offering one would fail at save time rather than at the point of choosing.
  const writable = platformProgrammes.filter(
    (programme) =>
      programme.visibilityState !== "locked" &&
      can(session.subject, "programme:write", { type: "programme", programmeId: programme.id }),
  );

  // Not a useState default: the tenant's programmes arrive after the first
  // render, so seeding from them once leaves the select empty for ever and the
  // form unsubmittable. The picked value wins; the first writable programme
  // stands in until then.
  const [pickedProgrammeId, setProgrammeId] = useState("");
  const programmeId = writable.some((programme) => programme.id === pickedProgrammeId)
    ? pickedProgrammeId
    : (writable[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [credits, setCredits] = useState("5");

  const create = useAsyncAction(async () => {
    const record = await client.loadProgramme(programmeId);
    if (!record) {
      throw new Error("That programme is no longer available.");
    }
    const module: Module = {
      id: uid("mod"),
      code: code.trim(),
      title: title.trim(),
      credits: Number(credits),
      isElective: false,
      mimlos: [],
      assessments: [],
    };
    await client.createModule({
      programmeId: record.id,
      module,
      expectedRevision: record.saveRevision,
    });
    // As in CreateProgrammeRoute: module ownership lands on the subject, which
    // is what the workspace checks before letting anyone edit it. Resolving
    // the session re-fires the workspace fetch, so that is not called too.
    await refreshSession();
    navigate(`/programmes/${record.id}/modules/${module.id}`);
  });

  return (
    <CreateModuleForm
      programmes={writable.map((programme) => ({
        id: programme.id,
        title: programme.title,
        programmeCode: programme.programmeCode,
      }))}
      programmeId={programmeId}
      onProgrammeChange={setProgrammeId}
      title={title}
      onTitleChange={setTitle}
      code={code}
      onCodeChange={setCode}
      credits={credits}
      onCreditsChange={setCredits}
      submitting={create.pending}
      error={create.error?.message}
      onSubmit={() => {
        create.run().catch(() => undefined);
      }}
      onCancel={() => {
        navigate("/programmes");
      }}
      teamAction={
        profile.role === "programme-lead" && programmeId ? (
          <Button
            icon="users-three"
            onClick={() => {
              navigate(`/programmes/${programmeId}/team`);
            }}
            data-testid="new-module-team-link"
          >
            Programme team
          </Button>
        ) : null
      }
    />
  );
}
